// @ts-nocheck
// Incorrect infer type with React.isValidElement wait react type fix
// https://github.com/microsoft/TypeScript/issues/53178#issuecomment-1495014731
import React, { FC, ReactElement, useState } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useMeasure } from "react-use";
import { Tooltip } from "@mui/material";

import { DateFormat, Status } from "@/components/atoms";
import { StatusColorType } from "@/components/atoms/Status/styled";
import { useGlobalFilter } from "@/shared/hooks";
import { StyledBottomSheetButton } from "@/styles/styled";

import BottomSheet, { BottomSheetData } from "../../BottomSheet/BottomSheet";
import { CardInfo } from "./components/CardInfo.interface";
import { StyledCard } from "./styled";

type Chip = {
    label: string;
    tooltip: string;
};

export type CardProps = {
    owner?: string;
    title: string;
    text?: string;
    dataTestId?: string;
    clickable?: boolean;
    date?: Date | string;
    reference?: string;
    className?: string;
    statusText?: string;
    statusColor?: StatusColorType;
    companyName?: string;
    companyIcon?: string;
    textBeforeDate?: string;
    onClickCard?: () => void;
    children?: React.ReactNode;
    infos?: Array<CardInfo | ReactElement>;
    categoryIcon?: ReactElement | null;
    forceOwnerDisplay?: boolean;
    chip?: Chip;
};

const COMPONENT_LENGTH = 768;

const Card: FC<CardProps> = ({
    date,
    owner,
    text,
    title,
    infos,
    children,
    className,
    reference,
    clickable = true,
    statusText,
    dataTestId,
    statusColor,
    companyIcon,
    companyName,
    categoryIcon,
    textBeforeDate,
    forceOwnerDisplay = false,
    chip,
    onClickCard,
}) => {
    const { t } = useTranslation();
    const { isProEnvironment } = useGlobalFilter();
    const [ref, { width }] = useMeasure();
    const [bottomSheetData, setBottomSheetData] = useState<BottomSheetData>(null);

    const isSmallCard = width <= COMPONENT_LENGTH;

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            onClickCard();
        }
    };

    const handleSeeInfo = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setBottomSheetData({ title: chip.label, content: chip.tooltip });
    };

    const dontDisplayOwner = !owner || (!forceOwnerDisplay && isProEnvironment);

    return (
        <StyledCard
            tabIndex={clickable ? 0 : -1}
            role="button"
            $notClickable={!clickable}
            $smallMinHeight={dontDisplayOwner && !categoryIcon}
            $noOwner={dontDisplayOwner}
            $hasBrokerLogo={!!companyIcon}
            $smallCard={width <= 360}
            className={clsx("card", className, width <= 360 && "smallCard")}
            onClick={onClickCard}
            ref={ref}
            data-testid={dataTestId}
            onKeyDown={handleKeyDown}
        >
            <div className="topBar">
                {!dontDisplayOwner && (
                    <div className={clsx("owner", isSmallCard && "smallLabel")} data-testid="card-owner">
                        {!isSmallCard && <div className="ownerLabel"> {t("Common.Policyholder")}</div>}
                        <div className="ownerName highlightText">{owner}</div>
                    </div>
                )}
                <div className="topBarRight">
                    {statusText && (
                        <Status
                            data-testid="card-status"
                            text={statusText}
                            color={statusColor || StatusColorType.Success}
                            className="status"
                        />
                    )}
                    {date && (
                        <div className="date" data-testid="card-date">
                            {textBeforeDate && <span className="beforeText">{`${textBeforeDate} `}</span>}
                            <span className="item-text highlightText">
                                {(date instanceof Date && <DateFormat date={date} />) || (
                                    <span className="item-text highlightText">{date as string}</span>
                                )}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="contentWrapper">
                <div
                    className={clsx(
                        "bodyWrapper",
                        categoryIcon && "bodyWrapperWithCategoryIcon",
                        companyIcon && "bodyWrapperWithCompanyIcon",
                    )}
                >
                    {companyIcon && (
                        <div className="companyImage">
                            <img src={companyIcon} alt={companyName || ""} />
                        </div>
                    )}

                    <div className="body">
                        <div className="titleLine">
                            {title && (
                                <span className="bodyTitle highlightText" data-testid="card-title">
                                    {title}
                                </span>
                            )}
                            {reference && (
                                <span className="bodyReference highlightText" data-testid="card-reference">
                                    {reference}
                                </span>
                            )}
                            {chip && (
                                <>
                                    <Tooltip
                                        title={chip.tooltip}
                                        placement="bottom-start"
                                        arrow
                                    >
                                        <span className="chip">{chip.label}</span>
                                    </Tooltip>
                                    <StyledBottomSheetButton
                                        $hideDesktop
                                        $inline={false}
                                        onClick={handleSeeInfo}
                                    >
                                        {t("Common.MoreInformations")}
                                    </StyledBottomSheetButton>
                                </>
                            )}
                            {bottomSheetData && (
                                <BottomSheet
                                    title={bottomSheetData.title}
                                    content={bottomSheetData.content}
                                    handleOverlayClick={() => setBottomSheetData(null)}
                                />
                            )}
                        </div>
                        {text && <span className="bodyText highlightText">{text}</span>}

                        {infos && (
                            <div className="infos">
                                {infos.map((info) =>
                                    React.isValidElement(info) ? (
                                        info
                                    ) : (
                                        <div
                                            className="info-block"
                                            key={`info-block-${info.title}`}
                                            data-testid={`info-block-${info.title}`}
                                        >
                                            <span className="info-label">{info.title}</span>
                                            <span className="info-value highlightText">{info.content}</span>
                                        </div>
                                    ),
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {children && <div className="description">{children}</div>}
            </div>
            {categoryIcon && <div className="categoryIcon">{categoryIcon}</div>}
        </StyledCard>
    );
};

export default Card;
